import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Bell, Eye, Gauge } from 'lucide-react';
import ModuleContainer from '../../layout/ModuleContainer.jsx';
import InfoButton from '../../common/InfoButton.jsx';

function ApiStatus({ label, isConnected }) {
  return (
    <div className="flex items-center justify-between px-3 py-2 bg-korinth-bg rounded border border-korinth-border/30">
      <span className="text-xs font-mono text-korinth-text">{label}</span>
      {isConnected
        ? <span className="flex items-center gap-1 text-[10px] font-mono text-korinth-accent"><CheckCircle className="w-3 h-3" /> Connected</span>
        : <span className="flex items-center gap-1 text-[10px] font-mono text-korinth-danger"><XCircle className="w-3 h-3" /> Missing</span>}
    </div>
  );
}

const SETTINGS_KEY = 'korinth_futures_settings';

const DEFAULT_SETTINGS = {
  shadowMode: true,
  telegramEnabled: true,
  minScoreAlert: 7,
  instruments: { NQ: true, ES: true, MNQ: false, GOLD: true, BTC: true },
  timezone: 'Asia/Dubai',
};

function loadSettings() {
  try {
    const saved = localStorage.getItem(SETTINGS_KEY);
    if (saved) return { ...DEFAULT_SETTINGS, ...JSON.parse(saved) };
  } catch { /* silent */ }
  return DEFAULT_SETTINGS;
}

export default function SettingsTab() {
  const [health, setHealth] = useState(null);
  const [settings, setSettings] = useState(loadSettings);

  // Persist to localStorage on every change
  useEffect(() => {
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings)); }
    catch { /* silent */ }
  }, [settings]);

  useEffect(() => {
    async function fetchHealth() {
      try {
        const res = await fetch('/api/health');
        if (res.ok) setHealth(await res.json());
      } catch { /* silent */ }
    }
    fetchHealth();
  }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* API Status */}
      <ModuleContainer title="API Connections" subtitle="Status of all data source connections">
        <div className="space-y-2">
          <ApiStatus label="Twelve Data (Prices)" isConnected={true} />
          <ApiStatus label="Binance (Crypto)" isConnected={true} />
          <ApiStatus label="NewsAPI (Headlines)" isConnected={true} />
          <ApiStatus label="Finnhub (News + Congress)" isConnected={true} />
          <ApiStatus label="Claude API (Classification)" isConnected={!!health} />
          <ApiStatus label="Telegram Bot (Alerts)" isConnected={true} />
          <ApiStatus label="Alternative.me (Fear & Greed)" isConnected={true} />
        </div>
        {health && (
          <div className="mt-3 text-[10px] font-mono text-korinth-muted">
            Server v{health.version} | Sprint {health.sprint} | Uptime: {(health.uptime / 3600).toFixed(1)}h
          </div>
        )}
      </ModuleContainer>

      {/* Instrument Settings */}
      <ModuleContainer title="Active Instruments" subtitle="Choose which instruments to track and receive signals for">
        <div className="space-y-2">
          {[
            { key: 'NQ', label: 'NQ — Nasdaq Futures', desc: 'E-mini Nasdaq 100' },
            { key: 'ES', label: 'ES — S&P 500 Futures', desc: 'E-mini S&P 500' },
            { key: 'MNQ', label: 'MNQ — Micro NQ', desc: 'Micro E-mini Nasdaq (same data as NQ, smaller size)' },
            { key: 'GOLD', label: 'Gold — XAU/USD', desc: 'Gold futures via XAU/USD' },
            { key: 'BTC', label: 'Bitcoin — BTC/USDT', desc: 'Bitcoin via Binance' },
          ].map(inst => (
            <label key={inst.key} className="flex items-center justify-between px-3 py-2 bg-korinth-bg rounded border border-korinth-border/30 cursor-pointer hover:border-korinth-accent/30">
              <div>
                <span className="text-xs font-mono text-white">{inst.label}</span>
                <p className="text-[9px] text-korinth-muted">{inst.desc}</p>
              </div>
              <input
                type="checkbox"
                checked={settings.instruments[inst.key] || false}
                onChange={() => setSettings(s => ({
                  ...s,
                  instruments: { ...s.instruments, [inst.key]: !s.instruments[inst.key] },
                }))}
                className="w-4 h-4 rounded border-korinth-border accent-korinth-accent"
              />
            </label>
          ))}
        </div>
      </ModuleContainer>

      {/* Notification Settings */}
      <ModuleContainer title="Notifications" subtitle="Configure when and how you receive alerts">
        <div className="space-y-3">
          <label className="flex items-center justify-between px-3 py-2 bg-korinth-bg rounded border border-korinth-border/30">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-korinth-warning" />
              <span className="text-xs font-mono text-white">Telegram Alerts</span>
              <InfoButton title="Telegram Alerts"><p>When enabled, HIGH conviction signals are pushed to your Telegram chat immediately.</p></InfoButton>
            </div>
            <input type="checkbox" checked={settings.telegramEnabled}
              onChange={() => setSettings(s => ({ ...s, telegramEnabled: !s.telegramEnabled }))}
              className="w-4 h-4 rounded accent-korinth-accent" />
          </label>

          <div className="px-3 py-2 bg-korinth-bg rounded border border-korinth-border/30">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Gauge className="w-4 h-4 text-korinth-accent" />
                <span className="text-xs font-mono text-white">Min Score for Alert</span>
                <InfoButton title="Score Threshold"><p>Only signals scoring at or above this threshold trigger Telegram alerts. Range: 5-10. Default: 7.</p></InfoButton>
              </div>
              <span className="text-xs font-mono text-korinth-accent font-bold">{settings.minScoreAlert}/10</span>
            </div>
            <input type="range" min="5" max="10" value={settings.minScoreAlert}
              onChange={e => setSettings(s => ({ ...s, minScoreAlert: parseInt(e.target.value) }))}
              className="w-full h-1.5 accent-korinth-accent" />
          </div>

          <label className="flex items-center justify-between px-3 py-2 bg-korinth-bg rounded border border-korinth-border/30">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-purple-400" />
              <span className="text-xs font-mono text-white">Shadow Mode</span>
              <InfoButton title="Shadow Mode"><p>When ON, all signals are for practice only. No real trades. Build conviction before going live.</p></InfoButton>
            </div>
            <input type="checkbox" checked={settings.shadowMode}
              onChange={() => setSettings(s => ({ ...s, shadowMode: !s.shadowMode }))}
              className="w-4 h-4 rounded accent-korinth-accent" />
          </label>
        </div>
      </ModuleContainer>

      {/* Display Settings */}
      <ModuleContainer title="Display" subtitle="Timezone and visual preferences">
        <div className="space-y-3">
          <div className="px-3 py-2 bg-korinth-bg rounded border border-korinth-border/30">
            <label className="text-xs font-mono text-korinth-muted block mb-1">Timezone</label>
            <select value={settings.timezone}
              onChange={e => setSettings(s => ({ ...s, timezone: e.target.value }))}
              className="w-full bg-korinth-surface border border-korinth-border rounded px-2 py-1.5 text-xs font-mono text-white focus:border-korinth-accent outline-none">
              <option value="Asia/Dubai">Dubai (UTC+4)</option>
              <option value="America/New_York">New York (ET)</option>
              <option value="America/Chicago">Chicago (CT)</option>
              <option value="Europe/London">London (GMT)</option>
              <option value="UTC">UTC</option>
            </select>
          </div>
          <p className="text-[9px] text-korinth-muted font-mono px-1">
            Settings are stored locally in your browser. Server-side settings coming in Sprint 10.
          </p>
        </div>
      </ModuleContainer>
    </div>
  );
}
